import React, { useEffect, useState } from 'react';
import {
  Stack,
  Box,
  Text,
  Flex,
  Center,
  IconButton,
  Image,
  Button,
  Divider,
  Spinner,
  useMediaQuery,
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import ReactGA from 'react-ga';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import { useTranslation } from 'react-i18next';
import { globalStore } from 'rekv';
import { useUserVestingInfo, getAddrState } from '../../api/query';
import NFTCards from '../../components/nftCards';
import Panel from '../../components/panel';
import ECharts from '../../components/echarts';
import curveUp from '../../assets/curve-up.svg';
import curveRight from '../../assets/curve-right.svg';
import divderImg from '../../assets/divider.jpg';
import userStore from '../../stores/account';
import { tokenMap } from '../../config/constants';
import { toast, formatBalance, toFixed } from '../../utils';
import { streamApi } from '../../api/contracts';
import { ToastProps } from '../../utils/types';
import cardBgImg from '../../assets/card-bg.png';

const Page = () => {
  const [isLargerThan960] = useMediaQuery('(min-width: 960px)');

  const location = useLocation();
  const history = useHistory();
  const params = useParams();
  const param: any = params;
  const vestingId = Number(param.id);
  const { t } = useTranslation();

  useEffect(() => {
    ReactGA.pageview(`/vesting/${vestingId}`);
  }, []);

  useEffect(() => {
    console.log(vestingId);
  }, [vestingId]);

  const { appState = {} } = globalStore.useState('appState');
  const { chainId } = appState;
  const [loading, setLoading] = useState(false);

  const addr = getAddrState();
  const { isLoading = true, refetch, isFetchedAfterMount } = useUserVestingInfo(addr);

  const {
    vestingTokenMap = {},
    tokenNumsMap = {},
    vestingMap,
  } = userStore.useState('vestingTokenMap', 'tokenNumsMap', 'vestingMap');
  // const { data: vestings } = useStreamInfo(100002, address);
  const vestingTokens: any = vestingTokenMap;
  const nfts = vestingTokens[vestingId];
  const vestings: any = vestingMap || {};
  const vestingInfo = vestings[vestingId] || {};
  const { claimable = 0, locked = 0, claimed = 0, tokenAddress = '' } = vestingInfo;
  const tokenConf = tokenMap[tokenAddress] || {};
  const { token = '', name = '' } = tokenConf;

  const getOptions = () => {
    return {
      colorBy: 'series',
      width: '280',
      height: '280',
      color: ['#00E0B5', '#D3FFF7'],
      series: [
        {
          type: 'pie',
          radius: ['100%', '90%'],
          hoverAnimation: false,

          itemStyle: {
            borderRadius: '100%',
            borderColor: '#fff',
            borderWidth: 2,
            shadowColor: 'red',
          },
          labelLine: {
            show: false,
          },
          data: [
            { value: claimable === 0 && locked === 0 ? null : toFixed(claimable) },
            { value: toFixed(locked) },
          ],
        },
      ],
    };
  };

  const handleClaim = async () => {
    try {
      // ReactGA.event({
      //   category: formType,
      //   action: `${formType} fund`,
      //   value: amount,
      // });
      const stream = streamApi();
      const toastProps: ToastProps = {
        title: 'Transaction',
        desc: '',
        status: 'success',
      };
      setLoading(true);
      const cbs = {
        receipt: () => {
          toastProps.desc = t(`claim.success`);
          toast(toastProps);
          setLoading(false);
          // ReactGA.event({
          //   category: formType,
          //   action: `${formType} fund success`,
          //   value: amount,
          // });

          refetch();
        },
        error: () => {
          setLoading(false);
        },
      };
      await stream.claimAll(vestingId, nfts, cbs);

      return null;
    } catch (e: any) {
      // console.log(error);
      const toastProps = {
        title: '',
        desc: '',
        duration: 6000,
        status: 'info',
      };
      toastProps.title = 'Error';
      toastProps.desc = e.message;
      toastProps.status = 'error';
      toast(toastProps);
      setLoading(false);
      return null;
    }
    // fetchData()
  };

  const handleHistory = () => {
    let url = '';
    if (chainId === 4) {
      url = `https://rinkeby.etherscan.io/token/${tokenAddress}?a=${addr}`;
    } else if (chainId === 100) {
      url = `https://blockscout.com/xdai/mainnet/address/${addr}/transactions`;
    }

    window.open(url, 'blank_');
  };

  return (
    <Panel mt={4}>
      {isLoading ? (
        <Center h="80vh">
          <Spinner />
        </Center>
      ) : tokenAddress ? (
        <Box>
          <Flex alignItems="center" mb={8}>
            <IconButton
              variant="unstyled"
              aria-label="Search database"
              onClick={() => {
                history.goBack();
              }}
              icon={<ChevronLeftIcon w="25px" h="25px" />}
            />{' '}
            <Text textStyle="title">{t('my.vesting.title', { name: token })}</Text>
          </Flex>
          <Flex zIndex={20} px={4} h="280px" mb={6}>
            <Box pos="relative" w="480px" h="480px">
              <ECharts
                zIndex={10}
                option={getOptions()}
                styles={{ height: '280px', width: '280px', backgroundColor: '#ffffff' }}
              />
              <Stack pos="absolute" left="20px" top="65px">
                <Text color="primary" textAlign="center" textStyle="label">
                  {t('my.claimable.title', { name: token })}
                </Text>
                <Center color="primary" textStyle="num">
                  <NumberFormat
                    value={toFixed(claimable)}
                    displayType={'text'}
                    thousandSeparator={true}
                    renderText={(value) => {
                      const numArr = value.split('.');
                      return (
                        <>
                          {numArr[0]}
                          {numArr[1] && (
                            <Text as="span" fontSize="16px">
                              .{numArr[1]}
                            </Text>
                          )}
                        </>
                      );
                    }}
                  />
                </Center>
                <Image w="240px" src={curveUp} />
                <Center textStyle="value">
                  <NumberFormat
                    value={toFixed(locked)}
                    displayType={'text'}
                    thousandSeparator={true}
                    renderText={(value) => {
                      const numArr = value.split('.');
                      return (
                        <>
                          {numArr[0]}
                          {numArr[1] && (
                            <Text as="span" fontSize="16px">
                              .{numArr[1]}
                            </Text>
                          )}
                        </>
                      );
                    }}
                  />
                </Center>
                <Text textStyle="label" textAlign="center">
                  {t('locked.title', { name: token })}
                </Text>
              </Stack>
            </Box>
            {isLargerThan960 && (
              <Box zIndex={20} ml="-180px">
                <Image w="150px" h="280px" src={curveRight} />
              </Box>
            )}
            <Flex pos="relative" w="70%" justifyContent="space-between" alignItems="center">
              <Button
                zIndex={40}
                ml="10px"
                h={'160'}
                w={'160px'}
                fontSize="24px"
                fontWeight="bold"
                borderWidth="5px"
                borderRadius="full"
                borderColor="#D3FFF7"
                onClick={handleClaim}
                _loading={{
                  bgColor: '#00D392',
                  opacity: 1,
                  borderWidth: '5px',
                  borderRadius: 'full',
                  borderColor: '#D3FFF7',
                }}
                isLoading={loading}
              >
                <Text>
                  CLAIM
                  <br />
                  ALL
                </Text>
              </Button>
              <Box textAlign="right" px={4} pb={5}>
                <Text textStyle="label"> {t('claimed.token', { token })}</Text>
                <Text as="div" textStyle="value" mb="20px">
                  <NumberFormat
                    value={toFixed(claimed)}
                    displayType={'text'}
                    thousandSeparator={true}
                    renderText={(value) => {
                      const numArr = value.split('.');
                      return (
                        <>
                          {numArr[0]}
                          {numArr[1] && (
                            <Text as="span" fontSize="16px">
                              .{numArr[1]}
                            </Text>
                          )}
                        </>
                      );
                    }}
                  />
                </Text>
                {/* <Box h='20px' /> */}
                <Text
                  cursor="pointer"
                  textColor="#999999"
                  textStyle="desc"
                  fontSize="14px"
                  onClick={handleHistory}
                >
                  {t('view.history')}
                </Text>
              </Box>
              <Divider borderColor="#00E0B5" zIndex={1} pos="absolute" />
            </Flex>
          </Flex>
          <Box bgColor="#ffffff" pos="relative" zIndex={30} mt={2} m={-4}>
            <Image w="100%" zIndex={30} h="114px" src={divderImg} />
            <Text w="100%" textAlign="center" textStyle="title" pos="absolute" top="25px" as="div">
              {t('nft.your')}
            </Text>
            <Box
              zIndex={50}
              my={-10}
              p={2.5}
              minH="300px"
              bgColor="rgba(255, 255, 255, 0.39)"
              bgImage={cardBgImg}
            >
              <NFTCards
                nfts={nfts}
                token={token}
                vestingId={vestingId}
                cb={refetch}
                vesting={vestingInfo}
              />
            </Box>
          </Box>
          <Box m={-4}>
            <Image
              zIndex={10}
              transform="rotateX(180deg)"
              w="100%"
              zIndex={30}
              h="114px"
              src={divderImg}
            />
          </Box>
        </Box>
      ) : (
        <Center bg="white" h="500px"></Center>
      )}
    </Panel>
  );
};

export default Page;
